import { useState, useRef, useEffect } from 'react';
import { Quote, Copy, Check, ChevronDown } from 'lucide-react';

const CitationButton = ({ note, isArabic }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const author = note.author || 'Unknown Author';
  const title = isArabic ? (note.title_ar || note.title) : note.title;
  const year = note.date ? note.date.replace(/[^0-9]/g, ' ').trim().split(/\s+/).pop() : new Date().getFullYear();
  const url = window.location.href;
  const accessDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const siteName = 'Decolonial Formations';

  const chicago = `${author}. "${title}." ${siteName}, ${note.date || year}. ${url}.`;
  const apa = `${author} (${year}). ${title}. ${siteName}. Retrieved ${accessDate}, from ${url}`;

  const handleCopy = async (text, style) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(style);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(style);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 caption link-subtle text-muted-foreground"
        data-testid="citation-button"
        aria-label={isArabic ? 'اقتبس هذه المقالة' : 'Cite this article'}
      >
        <Quote size={14} />
        {isArabic ? 'اقتباس' : 'Cite'}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute bottom-full mb-2 left-0 w-[360px] sm:w-[440px] bg-white border border-[#E5E0D6] shadow-lg z-50"
          style={{ direction: 'ltr', textAlign: 'left' }}
          data-testid="citation-dropdown"
        >
          {/* Chicago */}
          <div className="p-4 border-b border-[#E5E0D6]">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Chicago</span>
              <button
                onClick={() => handleCopy(chicago, 'chicago')}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[#8B6F47] transition-colors"
                data-testid="copy-chicago"
              >
                {copied === 'chicago' ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                {copied === 'chicago' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="text-sm leading-relaxed text-[#1C1C1C] font-sans select-all">{chicago}</p>
          </div>

          {/* APA */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">APA 7th</span>
              <button
                onClick={() => handleCopy(apa, 'apa')}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[#8B6F47] transition-colors"
                data-testid="copy-apa"
              >
                {copied === 'apa' ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                {copied === 'apa' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="text-sm leading-relaxed text-[#1C1C1C] font-sans select-all">{apa}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitationButton;
